import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, leadsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { bdaOptions } from '../utils/bdeOwnership';
import { parseContactBook } from '../utils/contactBook';
import { MEETING_TIME_ZONES, zonedWallClockToUtc, describeMeetingTime } from '../utils/timezones';
import { Paperclip, X, Users, Calendar, Video } from 'lucide-react';

/**
 * The meeting booking form, shared by the Meeting Schedule tab and the
 * pre-sales form so both behave identically.
 *
 * Attendees are picked, not typed: the AGS team on top (everyone in the roster,
 * so Shree, Siva and any manager are one click away), then the client's own
 * contacts pulled from that lead's POC fields and contact book. Anything else
 * can still be typed in.
 *
 * Someone can be kept off this list with `bde_users.exclude_from_attendees` —
 * a flag rather than names in the code, so the roster stays name-agnostic and
 * hiding or restoring a person is a database change, not a deploy. It hides
 * them from THIS picker only; they keep their account, their leads and every
 * filter.
 */

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve({
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      content: result.includes(',') ? result.split(',')[1] : result,
      size: file.size
    });
  };
  reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
  reader.readAsDataURL(file);
});

/** Every real address we hold for this lead: the POC fields plus the contact book. */
const clientContactsOf = (lead) => {
  if (!lead) return [];
  const seen = new Set();
  const out = [];
  const add = (email, name) => {
    const address = (email || '').trim().toLowerCase();
    if (!address || !address.includes('@') || seen.has(address)) return;
    seen.add(address);
    out.push({ email: address, name: (name || '').trim() });
  };
  add(lead.email, lead.poc_name);
  add(lead.poc_email, lead.poc_name);
  add(lead.poc2_email, lead.poc2_name);
  add(lead.poc3_email, lead.poc3_name);
  add(lead.poc4_email, lead.poc4_name);
  parseContactBook(lead.contact_book || '').forEach(entry => add(entry.email, entry.name));
  return out;
};

const MeetingScheduler = ({
  leadId,
  companyName,
  leadOptions = null,        // when set, the form also picks the company
  onScheduled,
  onCancel
}) => {
  const { user } = useAuth();
  const [roster, setRoster] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [files, setFiles] = useState([]);
  const [booked, setBooked] = useState(null); // the meeting just created

  const [form, setForm] = useState({
    lead_id: leadId || '',
    company_name: companyName || '',
    meeting_title: companyName ? `AGS × ${companyName}` : '',
    wall_clock: '',
    time_zone: 'America/New_York',
    duration_minutes: 30,
    agenda: '',
    extra_attendees: '',
    create_calendar_event: true
  });
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    dashboardAPI.getBDEUsers()
      .then(res => setRoster(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setRoster([]));
  }, []);

  // Companies to choose from. `leadOptions` comes from the page, but the
  // Meeting Schedule tab can be opened before the lead list has loaded, which
  // left an empty dropdown and no way to book anything.
  const [ownLeads, setOwnLeads] = useState(null);
  useEffect(() => {
    if (leadId) return undefined;                    // booking against a known lead
    if (leadOptions && leadOptions.length) return undefined;
    let cancelled = false;
    leadsAPI.getAll({})
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        if (!cancelled) setOwnLeads(list.filter(l => l && (l.company_name || '').trim()));
      })
      .catch(() => { if (!cancelled) setOwnLeads([]); });
    return () => { cancelled = true; };
  }, [leadId, leadOptions]);

  const companies = (leadOptions && leadOptions.length) ? leadOptions : (ownLeads || []);

  // The client's own people come from the lead record.
  const loadContacts = useCallback(async (id) => {
    if (!id) { setClientContacts([]); return; }
    try {
      setLoadingContacts(true);
      const res = await leadsAPI.getById(id);
      const lead = res?.data ?? res;
      setClientContacts(clientContactsOf(lead));
    } catch (e) {
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  useEffect(() => { loadContacts(form.lead_id); }, [form.lead_id, loadContacts]);

  const toggle = (email) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const flash = (type, text, ms = 7000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), ms);
  };

  const addFiles = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    try {
      const read = await Promise.all(chosen.map(readFileAsBase64));
      setFiles(prev => [...prev, ...read]);
    } catch (e) {
      flash('error', e.message);
    }
  };

  const submit = async (e) => {
    if (e) { e.preventDefault?.(); e.stopPropagation?.(); }
    if (!form.lead_id) return flash('error', 'Pick the company this meeting is for.');
    if (!form.wall_clock) return flash('error', 'Pick a date and time.');

    const typed = form.extra_attendees.split(/[,;\s]+/).filter(a => a.includes('@')).map(email => ({ email }));
    const chosen = [...picked].map(email => {
      const known = [...roster.map(r => ({ email: (r.email || '').toLowerCase(), name: r.name })), ...clientContacts]
        .find(c => c.email === email);
      return { email, name: known?.name || undefined };
    });
    const attendees = [...chosen, ...typed];

    const startIso = zonedWallClockToUtc(form.wall_clock, form.time_zone);
    if (!startIso) return flash('error', 'That date and time could not be read.');

    try {
      setSaving(true);
      const res = await dashboardAPI.createMeeting({
        lead_id: form.lead_id,
        company_name: form.company_name,
        meeting_title: form.meeting_title,
        agenda: form.agenda,
        meeting_notes: form.agenda,
        scheduled_at: startIso,
        time_zone: form.time_zone,
        duration_minutes: Number(form.duration_minutes) || 30,
        attendees,
        attachments: files.map(({ filename, mimeType, content }) => ({ filename, mimeType, content })),
        create_calendar_event: form.create_calendar_event,
        userId: user?.id,
        userEmail: user?.email
      });
      if (res.success) {
        setBooked(res.data || null);
        flash(res.warning ? 'error' : 'success',
          res.warning || (attendees.length
            ? `Scheduled for ${describeMeetingTime(startIso, form.time_zone)} — the invitation is on its way.`
            : `Scheduled for ${describeMeetingTime(startIso, form.time_zone)} — held in your calendar, nobody invited yet.`),
          20000);
        setFiles([]);
        setPicked(new Set());
        setForm(f => ({ ...f, wall_clock: '', agenda: '', extra_attendees: '' }));
        onScheduled?.(res.data);
      } else {
        flash('error', res.error || 'Could not schedule the meeting.');
      }
    } catch (err) {
      flash('error', err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const team = bdaOptions(roster).concat(
    (roster || []).filter(u => (u.role || '').toLowerCase() === 'admin')
      .map(u => ({ ...u, name: (u.name || '').trim() }))
  )
    .filter(u => u.exclude_from_attendees !== true)
    .filter((u, i, list) => u.email && list.findIndex(x => x.email === u.email) === i);

  const preview = form.wall_clock ? describeMeetingTime(zonedWallClockToUtc(form.wall_clock, form.time_zone), form.time_zone) : '';

  return (
    /* Deliberately a <div>, not a <form>: this is rendered inside the lead
       form on the pre-sales page, and a nested form is invalid HTML — the
       browser discards it, so "Schedule" submitted the LEAD form instead,
       saving the lead and closing the company without booking anything. */
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 space-y-4"
      onKeyDown={(e) => {
        // Enter in a field here must not submit the surrounding lead form either.
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {!leadId && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Company</label>
            <select
              value={form.lead_id}
              onChange={(e) => {
                const lead = companies.find(l => String(l.id) === e.target.value);
                setForm(f => ({
                  ...f,
                  lead_id: e.target.value,
                  company_name: lead?.company_name || '',
                  meeting_title: f.meeting_title || `AGS × ${lead?.company_name || ''}`
                }));
                setPicked(new Set());
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">{companies.length ? 'Select a company…' : 'Loading companies…'}</option>
              {companies.map(l => <option key={l.id} value={l.id}>{l.company_name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Subject <span className="font-normal text-gray-400">— what they see in their calendar</span>
          </label>
          <input
            type="text" value={form.meeting_title}
            onChange={(e) => setForm(f => ({ ...f, meeting_title: e.target.value }))}
            placeholder="AGS × Acme — intro call"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date &amp; time</label>
          <input
            type="datetime-local" value={form.wall_clock}
            onChange={(e) => setForm(f => ({ ...f, wall_clock: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Time zone</label>
          <select
            value={form.time_zone}
            onChange={(e) => setForm(f => ({ ...f, time_zone: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            title="The time above is read in this zone — everyone's invite lands at the right local time"
          >
            {MEETING_TIME_ZONES.map(z => <option key={z.id} value={z.id}>{z.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Duration (minutes)</label>
          <input
            type="number" min="15" step="15" value={form.duration_minutes}
            onChange={(e) => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      {preview && (
        <p className="text-xs text-[#4a6f22] bg-[#eaf3e0] border border-[#d4e6c4] rounded px-2 py-1.5">
          Starts {preview}. Each attendee sees it in their own zone.
        </p>
      )}

      {/* ---- attendees ---- */}
      <div className="border border-gray-200 rounded-lg">
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
          <Users size={14} className="text-gray-500" />
          <span className="text-xs font-medium text-gray-700">Attendees</span>
          <span className="text-xs text-gray-400">({picked.size} selected)</span>
        </div>

        <div className="p-3 space-y-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">AGS team</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {team.length === 0 && <span className="text-xs text-gray-400">Loading the team…</span>}
              {team.map(member => (
                <label key={member.email} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={picked.has((member.email || '').toLowerCase())}
                    onChange={() => toggle((member.email || '').toLowerCase())}
                    className="rounded border-gray-300 text-[#679632]"
                  />
                  {member.name}
                  {(member.role || '').toLowerCase() === 'admin' && (
                    <span className="text-[10px] px-1 rounded bg-gray-100 text-gray-500">admin</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-dashed border-gray-200 pt-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">
              Client contacts {form.company_name ? `at ${form.company_name}` : ''} — from the contact book
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {loadingContacts && <span className="text-xs text-gray-400">Reading the contact book…</span>}
              {!loadingContacts && clientContacts.length === 0 && (
                <span className="text-xs text-gray-400">
                  {form.lead_id ? 'No addresses on this lead yet.' : 'Pick a company to see its contacts.'}
                </span>
              )}
              {clientContacts.map(contact => (
                <label key={contact.email} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={picked.has(contact.email)}
                    onChange={() => toggle(contact.email)}
                    className="rounded border-gray-300 text-[#679632]"
                  />
                  {contact.name || contact.email}
                  {contact.name && <span className="text-xs text-gray-400">{contact.email}</span>}
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-dashed border-gray-200 pt-3">
            <label className="block text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">Anyone else</label>
            <input
              type="text" value={form.extra_attendees}
              onChange={(e) => setForm(f => ({ ...f, extra_attendees: e.target.value }))}
              placeholder="someone@company.com, another@company.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Agenda</label>
        <textarea
          rows={3} value={form.agenda}
          onChange={(e) => setForm(f => ({ ...f, agenda: e.target.value }))}
          placeholder="What the call will cover — this goes into the invitation the client receives."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Supporting documents</label>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs cursor-pointer hover:bg-gray-50">
            <Paperclip size={13} /> Attach files
            <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </label>
          {files.map((file, i) => (
            <span key={`${file.filename}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
              {file.filename}
              <span className="text-gray-400">{Math.round((file.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-600">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Sent with the invitation, so the deck arrives with the booking.</p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox" checked={form.create_calendar_event}
          onChange={(e) => setForm(f => ({ ...f, create_calendar_event: e.target.checked }))}
          className="rounded border-gray-300 text-[#679632]"
        />
        Create the Outlook event with a Teams link and invite everyone selected
      </label>

      {message.text && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {message.text}
        </div>
      )}

      {/* Straight after booking, show the meeting and its link here — no need to
          go looking for it to confirm it worked. */}
      {booked && (
        <div className="border border-[#679632] bg-[#f4f9ef] rounded-lg p-3 text-sm">
          <p className="font-medium text-[#496821]">
            {booked.meeting_title || 'Meeting'} · {describeMeetingTime(booked.scheduled_at, booked.time_zone)}
          </p>
          {(booked.attendees || []).length > 0 && (
            <p className="text-xs text-gray-600 mt-0.5">
              Invited: {(booked.attendees || []).map(a => a.email || a).join(', ')}
            </p>
          )}
          {booked.join_url ? (
            <a href={booked.join_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-[#0f6cbd] hover:underline">
              <Video size={14} /> Teams meeting link
            </a>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              Saved. No Teams link — the calendar event was not created for this mailbox.
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button" onClick={submit} disabled={saving}
          className="px-4 py-2 bg-[#679632] text-white rounded-lg text-sm font-medium hover:bg-[#587f2b] disabled:opacity-50 flex items-center gap-1.5"
        >
          <Calendar size={15} /> {saving ? 'Scheduling…' : 'Schedule meeting'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
        )}
      </div>
    </div>
  );
};

export default MeetingScheduler;
